import { CancelTokenSource } from 'axios';
import { useEffect, useRef, useState } from 'react';

import { AuthToken } from '@services/auth-token/authToken';
import { generateNewCancelTokenSource } from '@services/axios/axiosUtils';
import { BackOfficeAPIClient } from '@services/axios/clients/backOfficeAPIClient';
import { BillingOrderPayload, GetPaymentOrdersOptions } from '@shared/interfaces/backoffice';

export default function usePaymentInvoices({ fetchOnInit = true, ...restOptions }: GetPaymentOrdersOptions & { fetchOnInit?: boolean } = {}) {
    const {
        limit = 10,
        page = 1,
        sortBy = 'id',
        sortOrder = 'ASC',
        status = undefined
    } = restOptions;

    const [isLoading, setIsLoading]                     = useState(false);
    const [downloadLoading, setDownloadLoading]         = useState(false);
    const [hasError, setHasError]                       = useState(false);
    const [invoices, setInvoices]                       = useState<BillingOrderPayload>(null);
    const [downloadInvoiceData, setDownloadInvoiceData] = useState(null);
    const tokenSource                                   = useRef<CancelTokenSource>(null);

    useEffect(() => {
        if (!fetchOnInit) {
            return;
        }

        fetchInvoices({ limit, page, sortBy, sortOrder, status });
    }, [limit, page, sortBy, sortOrder, status]);

    const fetchInvoices = async (options: GetPaymentOrdersOptions = restOptions) => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.getOrders<BillingOrderPayload>(clientId, options, {
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setInvoices(response.data);
                setHasError(false);

                return response.data;
            }

            throw new Error('Failed to fetch orders!');
        } catch (e) {
            console.error(e);
            setHasError(true);

            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const downloadInvoice = async (orderReference: string) => {
        setDownloadLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.downloadInvoice(orderReference, {
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setDownloadInvoiceData(response.data);
                setHasError(false);

                return response.data;
            }

            throw new Error('Failed to download invoice!');
        } catch (e) {
            console.error(e);
            setHasError(true);

            return null;
        } finally {
            setDownloadLoading(false);
        }
    };

    return {
        isLoading,
        hasError,
        invoices,
        fetchInvoices,
        downloadLoading,
        downloadInvoice,
        downloadInvoiceData
    };
}
